import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Mail, Send, CheckCircle } from 'lucide-react';
import LegalFooter from '../components/LegalFooter';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // Simulate form submission
    // In production, this would send to a backend endpoint
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    setSubmitted(true);
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="contact-page">
      <header className="border-b border-zinc-200">
        <div className="container mx-auto px-8 py-6">
          <div className="flex items-center justify-between">
            <Link to="/" className="header-logo text-xl">AI Accountability Standards</Link>
            <nav className="flex items-center gap-6">
              <Link to="/registry" className="nav-link">Registry</Link>
              <Link to="/tools" className="nav-link">Tools</Link>
              <Link to="/org/login" className="text-sm text-zinc-500 hover:text-zinc-700">Organization Login</Link>
            </nav>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-8 py-12">
        <div className="max-w-2xl mx-auto">
          <h1 className="text-3xl font-semibold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Contact
          </h1>
          <p className="text-zinc-500 mb-8">AI Accountability Standards Council</p>

          {submitted ? (
            <div className="text-center py-12">
              <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-semibold mb-2">Message Received</h2>
              <p className="text-zinc-600 mb-6">
                Thank you for contacting the AI Accountability Standards Council. A representative will respond to your inquiry.
              </p>
              <Link to="/">
                <Button variant="outline" className="rounded-none">
                  Return to Home
                </Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="bg-zinc-50 border border-zinc-200 p-6 mb-8">
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-zinc-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Direct Contact</p>
                    <p className="text-zinc-600 text-sm mt-1">
                      For general inquiries, contact{' '}
                      <a href="mailto:contact@aiaccountabilitycouncil.com" className="text-zinc-900 underline">
                        contact@aiaccountabilitycouncil.com
                      </a>
                    </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Name</Label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="Full name"
                    className="rounded-none"
                    data-testid="contact-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email Address</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="email@organization.com"
                    className="rounded-none"
                    data-testid="contact-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">Message</Label>
                  <Textarea
                    id="message"
                    name="message"
                    required
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="How can we assist you?"
                    rows={6}
                    className="rounded-none resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="btn-primary rounded-none w-full"
                  disabled={loading}
                  data-testid="contact-submit"
                >
                  {loading ? (
                    'Sending...'
                  ) : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>

              <div className="mt-8 pt-8 border-t border-zinc-200">
                <h3 className="font-medium mb-4">Department Contacts</h3>
                <div className="space-y-3 text-sm text-zinc-600">
                  <p>
                    <span className="font-medium text-zinc-900">Declaration Inquiries:</span>{' '}
                    <a href="mailto:declarations@aiaccountabilitycouncil.com" className="underline">
                      declarations@aiaccountabilitycouncil.com
                    </a>
                  </p>
                  <p>
                    <span className="font-medium text-zinc-900">Enforcement:</span>{' '}
                    <a href="mailto:enforcement@aiaccountabilitycouncil.com" className="underline">
                      enforcement@aiaccountabilitycouncil.com
                    </a>
                  </p>
                  <p>
                    <span className="font-medium text-zinc-900">Privacy:</span>{' '}
                    <a href="mailto:privacy@aiaccountabilitycouncil.com" className="underline">
                      privacy@aiaccountabilitycouncil.com
                    </a>
                  </p>
                  <p>
                    <span className="font-medium text-zinc-900">Legal:</span>{' '}
                    <a href="mailto:legal@aiaccountabilitycouncil.com" className="underline">
                      legal@aiaccountabilitycouncil.com
                    </a>
                  </p>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
